import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Alert,
  CircularProgress,
  Chip,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AssignmentReturnIcon from '@mui/icons-material/AssignmentReturn';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import NfcIcon from '@mui/icons-material/Nfc';
import { useTranslation } from 'react-i18next';
import { clubEntryService, ClubEntrySaleCardDto, CardLookupResultDto } from '../../../../../services/clubEntryService';
import { cardManagementService } from '../../../../../services/cardManagementService';
import { processPartialReturn, getReturnableSale, cancelPavoPaySale } from '../../hooks/fiscalDevices/pavopay';
import type { PartialReturnSaleItem, PartialReturnPaymentInfo } from '../../hooks/fiscalDevices/pavopay';

interface CardReturnModalProps {
  open: boolean;
  onClose: () => void;
  depositEnabled: boolean;
  companyId?: string;
}

type Step = 'SCAN' | 'REVIEW' | 'PROCESSING' | 'DONE';

interface CardBalanceInfo {
  totalBalance: number;
  walletBalances: any[];
  sessionId: number | null;
  cardId: number | null;
  hasPosLoads: boolean;
  loadTransactions: Array<{ amount: number; paymentMethod: string; description?: string; createdAt: string }>;
}

// KARMA satışta (nakit giriş + POS depozito) iade fişinde 2 ödeme bulunur.
// Depozito POS ile alındığından eşlenikli iade NAKİT (mediator=1) ödemeyi değil
// POS/kart ödemesini hedeflemelidir; aksi halde cihaz "eşlenikli iade tutarı =
// nakit tutar" diyerek yanlış ödemeye referans veriyordu. PavoPay'de mediator 1
// = Nakit kabul edilir.
const pickDepositPosPayment = <
  T extends { id: number; mediatorId: number; amount: number; remainingVoidableAmount?: number }
>(
  payments: T[] | undefined,
  refundAmount: number,
): T | undefined => {
  if (!payments || payments.length === 0) return undefined;
  const nonCash = payments.filter((p) => p.mediatorId !== 1);
  // 1) Tutarı karşılayan POS/kart ödemesi (en güvenilir)
  const covering = nonCash.find(
    (p) => (p.remainingVoidableAmount ?? p.amount ?? 0) >= refundAmount - 0.01,
  );
  if (covering) return covering;
  // 2) Herhangi bir POS/kart ödemesi
  if (nonCash.length > 0) return nonCash[0];
  // 3) Mediator bilinmiyorsa tutarı eşleşen ödeme
  const byAmount = payments.find((p) => Math.abs((p.amount ?? 0) - refundAmount) < 0.01);
  if (byAmount) return byAmount;
  // 4) Son çare: ilk ödeme
  return payments[0];
};

const CardReturnModal: React.FC<CardReturnModalProps> = ({
  open,
  onClose,
  depositEnabled,
  companyId,
}) => {
  const { t } = useTranslation('salesSystem');
  const [step, setStep] = useState<Step>('SCAN');
  const [cardInput, setCardInput] = useState('');
  const [lookupResult, setLookupResult] = useState<CardLookupResultDto | null>(null);
  const [allCards, setAllCards] = useState<ClubEntrySaleCardDto[]>([]);
  const [returnedCardUIDs, setReturnedCardUIDs] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [processResults, setProcessResults] = useState<Array<{ cardUID: string; success: boolean; message: string }>>([]);
  const [processingMessage, setProcessingMessage] = useState('');
  const [cardBalanceMap, setCardBalanceMap] = useState<Record<string, CardBalanceInfo>>({});
  const [cashReturnDone, setCashReturnDone] = useState(false);
  const cardInputRef = useRef<HTMLInputElement>(null);

  const logPavo = useCallback((msg: string) => {
    console.log('[CardReturnModal PavoPay]', msg);
  }, []);

  useEffect(() => {
    if (open) {
      setStep('SCAN');
      setCardInput('');
      setLookupResult(null);
      setAllCards([]);
      setReturnedCardUIDs(new Set());
      setError(null);
      setWarnings([]);
      setProcessResults([]);
      setProcessingMessage('');
      setCardBalanceMap({});
      setCashReturnDone(false);
    }
  }, [open]);

  useEffect(() => {
    if (open && (step === 'SCAN' || step === 'REVIEW')) {
      const t = setTimeout(() => cardInputRef.current?.focus(), 150);
      return () => clearTimeout(t);
    }
  }, [open, step]);

  useEffect(() => {
    if (step === 'DONE' && processResults.length > 0 && processResults.every(r => r.success)) {
      const t = setTimeout(() => onClose(), 1000);
      return () => clearTimeout(t);
    }
  }, [step, processResults, onClose]);

  const handleLookup = useCallback(async () => {
    const uid = cardInput.trim();
    if (!uid) return;

    try {
      setLoading(true);
      setError(null);

      const result = await clubEntryService.lookupCard(uid);
      setLookupResult(result);

      const all = [result.card, ...result.relatedCards];
      setAllCards(all);
      setReturnedCardUIDs(new Set([uid]));

      if (companyId) {
        const balMap: Record<string, CardBalanceInfo> = {};
        const uniqueUIDs = Array.from(new Set(all.map(c => c.cardUID)));
        for (const cardUID of uniqueUIDs) {
          try {
            const cardRes = await cardManagementService.getCardByUID(companyId, cardUID);
            const card = cardRes.data?.data || cardRes.data;
            const cardId = card?.id || card?.Id;
            if (!cardId) continue;

            const sessionRes = await cardManagementService.getActiveSession(companyId, cardId);
            const session = sessionRes?.data?.data || sessionRes?.data;
            if (!session?.id) {
              balMap[cardUID] = { totalBalance: 0, walletBalances: [], sessionId: null, cardId, hasPosLoads: false, loadTransactions: [] };
              continue;
            }

            const walletBalances = session.walletBalances || [];
            const totalBalance = walletBalances.reduce((sum: number, w: any) => sum + (w.balance || 0), 0);

            let loadTransactions: CardBalanceInfo['loadTransactions'] = [];
            let hasPosLoads = false;
            try {
              const txRes = await cardManagementService.getSessionTransactions(session.id, companyId, 1, 50);
              const allTx = txRes.data?.data || [];
              loadTransactions = allTx
                .filter((tx: any) => tx.transactionType === 'LOAD')
                .map((tx: any) => ({
                  amount: tx.amount,
                  paymentMethod: tx.paymentMethod || 'CASH',
                  description: tx.description,
                  createdAt: tx.createdAt,
                }));
              hasPosLoads = loadTransactions.some(lt => lt.paymentMethod === 'POS');
            } catch (_) { /* transactions alınamadı */ }

            balMap[cardUID] = { totalBalance, walletBalances, sessionId: session.id, cardId, hasPosLoads, loadTransactions };
          } catch (_) { /* kart bulunamadı */ }
        }
        setCardBalanceMap(balMap);
      }

      const newWarnings: string[] = [];
      const hasPosDeposit = all.some(c => c.depositPavoPaySaleNumber && c.depositAmount > 0 && !c.depositRefunded && !c.isReturned);
      if (hasPosDeposit) {
        newWarnings.push(
          'POS ile alınan deposito iadesi için ödeme yapılan aynı banka/kredi kartının müşteride olması gerekmektedir.'
        );
        newWarnings.push(
          'Deposito iadesi ancak gün sonu yapılmadan önce gerçekleştirilebilir. Gün sonu yapıldıysa iade alınamaz.'
        );
      }
      setWarnings(newWarnings);
      setCardInput('');
      setStep('REVIEW');
      setTimeout(() => cardInputRef.current?.focus(), 100);
    } catch (err: any) {
      console.error('Kart arama hatası:', err);
      setError(err.response?.data?.message || 'Kart bulunamadı');
    } finally {
      setLoading(false);
    }
  }, [cardInput, companyId]);

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (step === 'SCAN') handleLookup();
      else if (step === 'REVIEW') handleScanAdditionalCard();
    }
  };

  const handleScanAdditionalCard = () => {
    const uid = cardInput.trim();
    if (!uid) return;
    const match = allCards.find(c => c.cardUID === uid);
    if (match) {
      setReturnedCardUIDs(prev => new Set(Array.from(prev).concat(uid)));
      setCardInput('');
      setError(null);
      setTimeout(() => cardInputRef.current?.focus(), 100);
    } else {
      setError('Bu kart bu satışa ait değil');
    }
  };

  const selectedCards = useMemo(() =>
    allCards.filter(c => returnedCardUIDs.has(c.cardUID) && !c.isReturned && (c.status === 'ACTIVE' || c.status === 'EXPIRED')),
    [allCards, returnedCardUIDs]
  );

  const hasPosBalance = useMemo(() =>
    selectedCards.some(c => {
      const bal = cardBalanceMap[c.cardUID];
      return bal && bal.totalBalance > 0 && bal.hasPosLoads;
    }),
    [selectedCards, cardBalanceMap]
  );

  const cashBalanceTotal = useMemo(() =>
    selectedCards.reduce((sum, c) => {
      const bal = cardBalanceMap[c.cardUID];
      if (bal && bal.totalBalance > 0 && !bal.hasPosLoads) return sum + bal.totalBalance;
      return sum;
    }, 0),
    [selectedCards, cardBalanceMap]
  );

  const posDepositTotal = useMemo(() =>
    selectedCards.filter(c => c.depositAmount > 0 && !c.depositRefunded && !!c.depositPavoPaySaleNumber)
      .reduce((sum, c) => sum + c.depositAmount, 0),
    [selectedCards]
  );

  const cashDepositTotal = useMemo(() =>
    selectedCards.filter(c => c.depositAmount > 0 && !c.depositRefunded && !c.depositPavoPaySaleNumber)
      .reduce((sum, c) => sum + c.depositAmount, 0),
    [selectedCards]
  );

  const totalCashRefund = cashBalanceTotal + cashDepositTotal;
  const needsMixedButtons = totalCashRefund > 0 && posDepositTotal > 0;

  const closeAndWithdraw = async (uid: string): Promise<string[]> => {
    const messages: string[] = [];
    const balInfo = cardBalanceMap[uid];

    if (balInfo?.sessionId && balInfo.totalBalance > 0 && !balInfo.hasPosLoads && companyId) {
      for (const wallet of balInfo.walletBalances) {
        if (wallet.balance > 0) {
          try {
            await cardManagementService.withdrawBalance({
              CardId: balInfo.cardId,
              WalletId: wallet.walletId,
              Amount: wallet.balance,
              Description: 'Kart teslim - nakit bakiye iadesi',
              CreatedBy: 'kart-teslim',
              ScreenId: '',
              PaymentMethod: 'CASH',
            });
            messages.push(`₺${wallet.balance.toFixed(2)} nakit bakiye iade edildi`);
          } catch (err: any) {
            messages.push(`Bakiye iade hatası: ${err.message}`);
          }
        }
      }
    }

    if (balInfo?.sessionId) {
      try {
        await cardManagementService.closeSession({
          SessionId: balInfo.sessionId,
          UserId: 'kart-teslim',
        });
      } catch (_) { /* oturum zaten kapalı olabilir */ }
    } else if (companyId) {
      try {
        const cardRes = await cardManagementService.getCardByUID(companyId, uid);
        const card = cardRes.data?.data || cardRes.data;
        const cardId = card?.id || card?.Id;
        if (cardId) {
          const sessionRes = await cardManagementService.getActiveSession(companyId, cardId);
          const session = sessionRes?.data?.data || sessionRes?.data;
          if (session?.id) {
            await cardManagementService.closeSession({ SessionId: session.id, UserId: 'kart-teslim' });
          }
        }
      } catch (_) {}
    }

    return messages;
  };

  const handleCashReturn = async () => {
    setStep('PROCESSING');
    setError(null);
    const results: Array<{ cardUID: string; success: boolean; message: string }> = [];

    for (const card of selectedCards) {
      const uid = card.cardUID;

      // ⚠️ POS depozito (Pavo satış no var) bekleyen kartları BURADA teslim ALMA.
      //    Önce CancelSale (satış iptali) başarılı olmalı; teslim alma + oturum
      //    kapatma handlePosDepositReturn adımında yapılır. Aksi halde iptal
      //    başarısız olsa bile kart kapanıp "iade alındı" görünür (HATA).
      if (depositEnabled && card.depositAmount > 0 && !card.depositRefunded && card.depositPavoPaySaleNumber) {
        continue;
      }

      setProcessingMessage(`${uid} işleniyor...`);
      try {
        await clubEntryService.returnCard(uid);

        const extraMsgs = await closeAndWithdraw(uid);

        // ⚠️ Depozito POS ile alınmış ama Pavo satış no yoksa otomatik POS iadesi
        // yapılamaz; sessizce "nakit iade edildi" işaretlemek YANLIŞTIR (depozito
        // gerçekte POS'tan alındı). Bu durumu nakit iadeden ayır ve kullanıcıyı uyar.
        const isPosNoSaleNumber = card.depositPaymentMethod === 'POS' && !card.depositPavoPaySaleNumber;
        if (depositEnabled && card.depositAmount > 0 && !card.depositRefunded && !card.depositPavoPaySaleNumber && !isPosNoSaleNumber) {
          await clubEntryService.markDepositRefunded(uid);
          const balMsg = extraMsgs.length > 0 ? `, ${extraMsgs.join(', ')}` : '';
          results.push({ cardUID: uid, success: true, message: `Kart teslim alındı, ₺${card.depositAmount.toFixed(2)} nakit deposito iade edildi${balMsg}` });
        } else {
          const balMsg = extraMsgs.length > 0 ? ` (${extraMsgs.join(', ')})` : '';
          const depMsg = card.depositPavoPaySaleNumber && card.depositAmount > 0 && !card.depositRefunded
            ? ' (POS deposito iadesi ayrıca yapılacak)'
            : isPosNoSaleNumber && card.depositAmount > 0 && !card.depositRefunded
            ? ' (⚠️ POS depozito fakat Pavo satış no yok — otomatik iade yapılmadı, manuel iade gerekli)'
            : card.depositRefunded ? ' (deposito daha önce iade edilmiş)' : '';
          results.push({ cardUID: uid, success: true, message: `Kart teslim alındı${depMsg}${balMsg}` });
        }
      } catch (err: any) {
        results.push({ cardUID: uid, success: false, message: err.response?.data?.message || 'İşlem hatası' });
      }
    }

    setProcessResults(prev => [...prev, ...results]);
    setProcessingMessage('');
    setCashReturnDone(true);

    if (posDepositTotal <= 0) {
      setStep('DONE');
    } else {
      setStep('REVIEW');
    }
  };

  const handlePosDepositReturn = async () => {
    setStep('PROCESSING');
    setError(null);
    const results: Array<{ cardUID: string; success: boolean; message: string }> = [];

    const posDepositCards = selectedCards.filter(c => c.depositAmount > 0 && !c.depositRefunded && !!c.depositPavoPaySaleNumber);
    logPavo(`POS depozito iade: ${posDepositCards.length} kart, fişler: ${Array.from(new Set(posDepositCards.map(c => c.depositPavoPaySaleNumber).filter(Boolean))).join(', ')}`);

    const saleNumberGroups: Record<string, ClubEntrySaleCardDto[]> = {};
    for (const card of posDepositCards) {
      const sn = card.depositPavoPaySaleNumber!;
      if (!saleNumberGroups[sn]) saleNumberGroups[sn] = [];
      saleNumberGroups[sn].push(card);
    }

    for (const [saleNumber, groupCards] of Object.entries(saleNumberGroups)) {
      // ✅ Depozito artık AYRI bir satış → TAM İPTAL (CancelSale). Aynı depozito
      //    satış no'sundaki kartlar tek iptalle birlikte iade edilir.
      const groupTotal = groupCards.reduce((s, c) => s + (c.depositAmount || 0), 0);
      setProcessingMessage(`Depozito satışı ${saleNumber} iptal ediliyor (${groupCards.length} kart)...`);
      logPavo(`CancelSale (tam iptal): saleNumber=${saleNumber}, kart=${groupCards.length}, tutar=${groupTotal}`);
      console.log(
        '%c[PAVO İSPAT] DEPOZİTO İADESİ → CancelSale (SATIŞ İPTALİ / VOID) çağrılıyor — İADE (PartialReturn) DEĞİL',
        'background:#b71c1c;color:#fff;font-weight:bold;padding:2px 6px;border-radius:3px;',
        {
          islem: 'cancelPavoPaySale()',
          orijinalDepozitoSatisNo: saleNumber,
          iptalEdilenTutar: groupTotal,
          kartSayisi: groupCards.length,
          aciklama: 'Sistem, POS ile alınmış depozitoyu iade ederken yeni bir iade satışı yapmıyor; orijinal depozito satışının ÖDEMESİNİ İPTAL (CancelSale / void) ediyor.',
        },
      );

      try {
        const cancelRes = await cancelPavoPaySale(saleNumber, companyId || '', groupTotal);
        logPavo(`CancelSale sonucu: success=${cancelRes.success}, error=${cancelRes.errorMessage || '-'}`);

        if (cancelRes.success) {
          // ✅ Sadece iptal BAŞARILI olduğunda kartı teslim al + oturumu kapat.
          // ⚠️ Cihaz iptali başarılı olsa da muhasebe iade kaydı oluşturulamadıysa
          //    (backendRefundSuccess=false) bunu YEŞİL başarı gibi gösterme — uyar.
          const accountingFailed = groupTotal > 0 && cancelRes.backendRefundSuccess === false;
          for (const card of groupCards) {
            try {
              await clubEntryService.returnCard(card.cardUID);
              const extraMsgs = await closeAndWithdraw(card.cardUID);
              await clubEntryService.markDepositRefunded(card.cardUID);
              const balMsg = extraMsgs.length > 0 ? `, ${extraMsgs.join(', ')}` : '';
              if (accountingFailed) {
                results.push({ cardUID: card.cardUID, success: false, message: `Kart teslim alındı ve POS satışı iptal edildi (₺${card.depositAmount.toFixed(2)} müşteriye iade edildi) ANCAK muhasebe iade kaydı oluşturulamadı — bağlantıyı kontrol edip tekrar deneyin${balMsg}` });
              } else {
                results.push({ cardUID: card.cardUID, success: true, message: `Kart teslim alındı, ₺${card.depositAmount.toFixed(2)} POS depozito iade edildi (satış iptali)${balMsg}` });
              }
            } catch (closeErr: any) {
              // İptal Pavo'da yapıldı ama teslim/oturum kapatma hata verdi.
              results.push({ cardUID: card.cardUID, success: false, message: `Depozito iptal edildi fakat kart teslim/oturum kapatma hatası: ${closeErr?.message || 'bilinmeyen hata'}` });
            }
          }
        } else {
          // ⚠️ İptal başarısız → kart TESLİM ALINMADI, tekrar denenebilir.
          const rawErr = (cancelRes.errorMessage || '').toLowerCase();
          const looksNotFound = !rawErr || rawErr.includes('bulunamad') || rawErr.includes('not found');
          const hint = looksNotFound
            ? `satış Pavo sunucusunda bulunamadı — cihaz çevrimdışıyken kesilmiş olabilir, internete bağlanıp senkron olduktan sonra tekrar deneyin`
            : (cancelRes.errorMessage || 'iptal başarısız');
          for (const card of groupCards) {
            results.push({ cardUID: card.cardUID, success: false, message: `POS depozito iptali başarısız (${hint}) — kart TESLİM ALINMADI` });
          }
        }
      } catch (refundErr: any) {
        logPavo(`Exception: ${refundErr?.message || String(refundErr)}`);
        // ⚠️ İptal sırasında hata → kart TESLİM ALINMADI, tekrar denenebilir.
        for (const card of groupCards) {
          results.push({ cardUID: card.cardUID, success: false, message: `POS depozito iptal hatası (${refundErr.message}) — kart TESLİM ALINMADI, tekrar deneyin` });
        }
      }
    }

    setProcessResults(prev => [...prev, ...results]);
    setProcessingMessage('');
    setStep('DONE');
  };

  const handleAllInOneReturn = async () => {
    setStep('PROCESSING');
    setError(null);
    const results: Array<{ cardUID: string; success: boolean; message: string }> = [];

    // Adım 1: Tüm kartları teslim al, oturumları kapat ve nakit iadeleri yap
    // POS iadelerini sonra fiş numarasına göre gruplayarak yapacağız
    const posRefundPending: Array<{ card: ClubEntrySaleCardDto; extraMsgs: string[] }> = [];

    for (const card of selectedCards) {
      const uid = card.cardUID;

      // ✅ POS depozito (eşlenikli iade): kartı teslim ALMADAN önce iadeyi dene.
      //    İade "satış bulunamadı" vb. ile başarısız olursa kart AKTİF kalır;
      //    operatör tekrar deneyebilir. (Önceden kart Adım 1'de hemen teslim
      //    alınıp POS iadesi Adım 2'de deneniyordu → başarısızlıkta kart kapanıp
      //    "iade alındı" görünüyordu. Artık iade Adım 2'de başarılı olursa
      //    teslim alma + oturum kapatma orada yapılır.)
      if (depositEnabled && card.depositAmount > 0 && !card.depositRefunded && card.depositPavoPaySaleNumber) {
        posRefundPending.push({ card, extraMsgs: [] });
        continue;
      }

      setProcessingMessage(`${uid} teslim alınıyor...`);
      try {
        await clubEntryService.returnCard(uid);
        const extraMsgs = await closeAndWithdraw(uid);

        if (depositEnabled && card.depositAmount > 0 && !card.depositRefunded) {
          if (card.depositPaymentMethod === 'POS') {
            // POS depozito ama Pavo satış no yok → otomatik POS iadesi yapılamaz.
            // Nakit iade işaretlemek yanlış olur; kullanıcıyı uyar (deposito iade edilmedi).
            const balMsg = extraMsgs.length > 0 ? `, ${extraMsgs.join(', ')}` : '';
            results.push({ cardUID: uid, success: false, message: `Kart teslim alındı fakat ₺${card.depositAmount.toFixed(2)} POS depozito otomatik iade edilemedi (Pavo satış no yok) — manuel iade gerekli${balMsg}` });
          } else {
            await clubEntryService.markDepositRefunded(uid);
            const balMsg = extraMsgs.length > 0 ? `, ${extraMsgs.join(', ')}` : '';
            results.push({ cardUID: uid, success: true, message: `Kart teslim alındı, ₺${card.depositAmount.toFixed(2)} nakit deposito iade edildi${balMsg}` });
          }
        } else {
          const balMsg = extraMsgs.length > 0 ? ` (${extraMsgs.join(', ')})` : '';
          results.push({ cardUID: uid, success: true, message: `Kart teslim alındı${balMsg}` });
        }
      } catch (err: any) {
        results.push({ cardUID: uid, success: false, message: err.response?.data?.message || 'İşlem hatası' });
      }
    }

    // Adım 2: POS depozito iadeleri — depozito artık AYRI bir satış olduğundan
    //   TAM İPTAL (CancelSale) ile yapılır. Aynı depozito satış no'sundaki kartlar
    //   tek iptalle birlikte iade edilir. Sadece iptal BAŞARILI olunca kart teslim
    //   alınır + oturum kapatılır; başarısızsa kart AKTİF kalır (tekrar denenebilir).
    if (posRefundPending.length > 0) {
      const saleNumberGroups: Record<string, Array<{ card: ClubEntrySaleCardDto; extraMsgs: string[] }>> = {};
      for (const item of posRefundPending) {
        const sn = item.card.depositPavoPaySaleNumber!;
        if (!saleNumberGroups[sn]) saleNumberGroups[sn] = [];
        saleNumberGroups[sn].push(item);
      }

      for (const [saleNumber, groupItems] of Object.entries(saleNumberGroups)) {
        const groupTotal = groupItems.reduce((s, { card }) => s + (card.depositAmount || 0), 0);
        setProcessingMessage(`Depozito satışı ${saleNumber} iptal ediliyor (${groupItems.length} kart)...`);
        logPavo(`CancelSale (tam iptal): saleNumber=${saleNumber}, kart=${groupItems.length}, tutar=${groupTotal}`);
        console.log(
          '%c[PAVO İSPAT] DEPOZİTO İADESİ → CancelSale (SATIŞ İPTALİ / VOID) çağrılıyor — İADE (PartialReturn) DEĞİL',
          'background:#b71c1c;color:#fff;font-weight:bold;padding:2px 6px;border-radius:3px;',
          {
            islem: 'cancelPavoPaySale()',
            orijinalDepozitoSatisNo: saleNumber,
            iptalEdilenTutar: groupTotal,
            kartSayisi: groupItems.length,
            aciklama: 'Sistem, POS ile alınmış depozitoyu iade ederken yeni bir iade satışı yapmıyor; orijinal depozito satışının ÖDEMESİNİ İPTAL (CancelSale / void) ediyor.',
          },
        );

        try {
          const cancelRes = await cancelPavoPaySale(saleNumber, companyId || '', groupTotal);
          logPavo(`CancelSale sonucu: success=${cancelRes.success}, error=${cancelRes.errorMessage || '-'}`);

          if (cancelRes.success) {
            // ✅ Sadece iptal BAŞARILI olduğunda kartı teslim al + oturumu kapat.
            // ⚠️ Cihaz iptali başarılı olsa da muhasebe iade kaydı oluşturulamadıysa
            //    (backendRefundSuccess=false) bunu YEŞİL başarı gibi gösterme — uyar.
            const accountingFailed = groupTotal > 0 && cancelRes.backendRefundSuccess === false;
            for (const { card } of groupItems) {
              try {
                await clubEntryService.returnCard(card.cardUID);
                const extraMsgs = await closeAndWithdraw(card.cardUID);
                await clubEntryService.markDepositRefunded(card.cardUID);
                const balMsg = extraMsgs.length > 0 ? `, ${extraMsgs.join(', ')}` : '';
                if (accountingFailed) {
                  results.push({ cardUID: card.cardUID, success: false, message: `Kart teslim alındı ve POS satışı iptal edildi (₺${card.depositAmount.toFixed(2)} müşteriye iade edildi) ANCAK muhasebe iade kaydı oluşturulamadı — bağlantıyı kontrol edip tekrar deneyin${balMsg}` });
                } else {
                  results.push({ cardUID: card.cardUID, success: true, message: `Kart teslim alındı, ₺${card.depositAmount.toFixed(2)} POS depozito iade edildi (satış iptali)${balMsg}` });
                }
              } catch (closeErr: any) {
                // İptal Pavo'da yapıldı ama teslim/oturum kapatma hata verdi.
                results.push({ cardUID: card.cardUID, success: false, message: `Depozito iptal edildi fakat kart teslim/oturum kapatma hatası: ${closeErr?.message || 'bilinmeyen hata'}` });
              }
            }
          } else {
            // ⚠️ İptal başarısız → kart TESLİM ALINMADI, tekrar denenebilir.
            // 🔎 "satış bulunamadı" → çevrimdışı kesilip henüz senkron olmamış olabilir.
            const rawErr = (cancelRes.errorMessage || '').toLowerCase();
            const looksNotFound = !rawErr || rawErr.includes('bulunamad') || rawErr.includes('not found');
            const hint = looksNotFound
              ? `satış Pavo sunucusunda bulunamadı — cihaz çevrimdışıyken kesilmiş olabilir, internete bağlanıp senkron olduktan sonra tekrar deneyin`
              : (cancelRes.errorMessage || 'iptal başarısız');
            for (const { card } of groupItems) {
              results.push({ cardUID: card.cardUID, success: false, message: `POS depozito iptali başarısız (${hint}) — kart TESLİM ALINMADI` });
            }
          }
        } catch (refundErr: any) {
          // ⚠️ İptal sırasında hata → kart TESLİM ALINMADI, tekrar denenebilir.
          for (const { card } of groupItems) {
            results.push({ cardUID: card.cardUID, success: false, message: `POS depozito iptal hatası (${refundErr.message}) — kart TESLİM ALINMADI, tekrar deneyin` });
          }
        }
      }
    }

    setProcessResults(results);
    setProcessingMessage('');
    setStep('DONE');
  };

  const getDepositMethodLabel = (card: ClubEntrySaleCardDto) => {
    if (card.depositAmount <= 0) return null;
    return card.depositPavoPaySaleNumber ? 'POS' : 'Nakit';
  };

  const getRefundActionLabel = (card: ClubEntrySaleCardDto) => {
    if (card.isReturned) return 'Zaten teslim edilmiş';
    if (card.depositAmount <= 0 || card.depositRefunded) {
      if (card.depositRefunded) return 'Deposito daha önce iade edilmiş';
      return 'Sadece teslim';
    }
    if (card.depositPavoPaySaleNumber) return `₺${card.depositAmount.toFixed(2)} POS deposito iade`;
    return `₺${card.depositAmount.toFixed(2)} nakit deposito iade`;
  };

  const unreturned = allCards.filter(c => !c.isReturned && (c.status === 'ACTIVE' || c.status === 'EXPIRED'));
  const notYetScanned = unreturned.filter(c => !returnedCardUIDs.has(c.cardUID));

  return (
    <Dialog open={open} onClose={step === 'PROCESSING' ? undefined : onClose} maxWidth={step === 'DONE' ? 'sm' : 'md'} fullWidth={step !== 'DONE'}>
      <DialogTitle sx={{ bgcolor: 'secondary.main', color: 'white', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AssignmentReturnIcon />
          <Typography variant="h6">{t('cardReturnModal.title')}</Typography>
        </Box>
        <IconButton color="inherit" onClick={onClose} disabled={step === 'PROCESSING'}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 2, mt: 1 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>
        )}

        {step === 'SCAN' && (
          <>
            <Typography variant="body1" sx={{ mb: 2 }}>
              {t('cardReturnModal.scanDescription')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField fullWidth size="small" label={t('cardReturnModal.cardUidLabel')} value={cardInput}
                inputRef={cardInputRef}
                onChange={(e) => setCardInput(e.target.value)} onKeyDown={handleKeyDown} autoFocus disabled={loading}
                InputProps={{ startAdornment: <CreditCardIcon sx={{ mr: 1, color: 'action.active' }} /> }}
              />
              <Button variant="contained" onClick={handleLookup} disabled={!cardInput.trim() || loading}>
                {loading ? <CircularProgress size={20} /> : t('cardReturnModal.search')}
              </Button>
            </Box>
            {loading && <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}><CircularProgress /></Box>}
          </>
        )}

        {step === 'REVIEW' && lookupResult && (
          <>
            {notYetScanned.length > 0 && !cashReturnDone && (
              <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover' }}>
                <Typography variant="subtitle2" gutterBottom>{t('cardReturnModal.scanCardsTitle')}</Typography>
                <Box sx={{ display: 'flex', gap: 1 }}>
                  <TextField fullWidth size="small" label={t('cardReturnModal.cardUidLabel')} value={cardInput}
                    inputRef={cardInputRef}
                    onChange={(e) => setCardInput(e.target.value)} onKeyDown={handleKeyDown} autoFocus
                    placeholder={t('cardReturnModal.cardUidPlaceholder')}
                    InputProps={{ startAdornment: <NfcIcon sx={{ mr: 1, color: 'action.active' }} /> }}
                  />
                  <Button variant="outlined" onClick={handleScanAdditionalCard} disabled={!cardInput.trim()}>{t('cardReturnModal.add')}</Button>
                </Box>
              </Paper>
            )}

            {warnings.map((w, i) => (
              <Alert key={i} severity="warning" icon={<WarningIcon />} sx={{ mb: 1 }}>{w}</Alert>
            ))}

            {hasPosBalance && (
              <Alert severity="error" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold">
                  Bu kart POS ile yüklenmiş bakiye içermektedir. POS ile yüklenen bakiyenin iadesi bu ekrandan yapılamaz.
                </Typography>
                <Typography variant="body2" sx={{ mt: 0.5 }}>
                  Lütfen önce PYK Ekranından kart bakiye iadesini yapın, ardından kartı teslim alın.
                </Typography>
              </Alert>
            )}

            {cashReturnDone && processResults.length > 0 && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight="bold">Nakit iade ve kart teslim işlemi tamamlandı.</Typography>
                <Typography variant="body2">Şimdi POS deposito iadesini yapabilirsiniz.</Typography>
              </Alert>
            )}

            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Satış Bilgileri</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  <strong>Fiş No:</strong> {lookupResult.card.saleReceiptNumber || lookupResult.card.saleReceiptId}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  <strong>Hizmet Ödeme:</strong> {lookupResult.card.paymentMethod === 'POS' ? 'POS' : 'Nakit'}
                </Typography>
                {lookupResult.card.pavoPaySaleNumber && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>Hizmet PavoPay No:</strong> {lookupResult.card.pavoPaySaleNumber}
                  </Typography>
                )}
                <Typography variant="body2" color="text.secondary">
                  <strong>Toplam Kart:</strong> {allCards.length}
                </Typography>
              </Box>
            </Paper>

            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'action.hover' }}>
                    <TableCell><strong>Kart UID</strong></TableCell>
                    <TableCell><strong>Hizmet</strong></TableCell>
                    <TableCell align="center"><strong>Deposito</strong></TableCell>
                    <TableCell align="center"><strong>Dep. Ödeme</strong></TableCell>
                    <TableCell align="center"><strong>Kart Bakiye</strong></TableCell>
                    <TableCell align="center"><strong>Konum</strong></TableCell>
                    <TableCell align="center"><strong>Durum</strong></TableCell>
                    <TableCell><strong>İade İşlemi</strong></TableCell>
                    <TableCell align="center"><strong>Teslim</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {allCards.map(card => {
                    const isSelected = returnedCardUIDs.has(card.cardUID);
                    const balInfo = cardBalanceMap[card.cardUID];
                    const cardHasPosBalance = balInfo && balInfo.totalBalance > 0 && balInfo.hasPosLoads;
                    return (
                      <TableRow key={card.id} sx={{ bgcolor: cardHasPosBalance ? 'error.50' : isSelected ? 'action.selected' : undefined }}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={isSelected ? 'bold' : 'normal'} sx={{ fontFamily: 'monospace' }}>
                            {card.cardUID}
                          </Typography>
                          {card.cardLabel && <Typography variant="caption" color="text.secondary">{card.cardLabel}</Typography>}
                        </TableCell>
                        <TableCell>{card.serviceProductName || '-'}</TableCell>
                        <TableCell align="center">
                          {card.depositAmount > 0 ? (
                            <Box>
                              <Typography variant="body2" fontWeight="bold">₺{card.depositAmount.toFixed(2)}</Typography>
                              {card.depositRefunded && (
                                <Chip size="small" label="İade Edildi" color="warning" sx={{ mt: 0.5, fontSize: '0.65rem', height: 20 }} />
                              )}
                            </Box>
                          ) : '-'}
                        </TableCell>
                        <TableCell align="center">
                          {card.depositAmount > 0 ? (
                            <Box>
                              <Chip size="small" label={getDepositMethodLabel(card)}
                                color={card.depositPavoPaySaleNumber ? 'primary' : 'default'} variant="outlined" />
                              {card.depositPavoPaySaleNumber && (
                                <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.3, fontSize: '0.6rem' }}>
                                  {card.depositPavoPaySaleNumber}
                                </Typography>
                              )}
                            </Box>
                          ) : '-'}
                        </TableCell>
                        <TableCell align="center">
                          {balInfo ? (
                            balInfo.totalBalance > 0 ? (
                              <Box>
                                <Typography variant="body2" fontWeight="bold" color={cardHasPosBalance ? 'error.main' : 'warning.main'}>
                                  ₺{balInfo.totalBalance.toFixed(2)}
                                </Typography>
                                {balInfo.loadTransactions.length > 0 && (
                                  <Box sx={{ mt: 0.3 }}>
                                    {balInfo.loadTransactions.map((lt, i) => (
                                      <Chip key={i} size="small"
                                        label={`${lt.paymentMethod === 'POS' ? 'POS' : 'Nakit'} ₺${lt.amount.toFixed(0)}`}
                                        color={lt.paymentMethod === 'POS' ? 'error' : 'default'}
                                        variant="outlined"
                                        sx={{ mr: 0.3, mb: 0.2, fontSize: '0.6rem', height: 18 }}
                                      />
                                    ))}
                                  </Box>
                                )}
                              </Box>
                            ) : <Typography variant="caption" color="text.secondary">₺0.00</Typography>
                          ) : <Typography variant="caption" color="text.secondary">-</Typography>}
                        </TableCell>
                        <TableCell align="center">
                          <Box>
                            <Chip size="small"
                              label={card.isInside ? `İçeride${card.insideCount > 1 ? ` (${card.insideCount})` : ''}` : 'Dışarıda'}
                              color={card.isInside ? 'success' : 'default'}
                              variant={card.isInside ? 'filled' : 'outlined'}
                            />
                            {card.lastPassTime && (
                              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 0.3, fontSize: '0.6rem' }}>
                                {card.lastPassDirection === 'entry' ? 'Giriş' : 'Çıkış'}: {new Date(card.lastPassTime.endsWith('Z') ? card.lastPassTime : card.lastPassTime + 'Z').toLocaleTimeString('tr-TR')}
                              </Typography>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          {card.isReturned
                            ? <Chip size="small" label="Teslim Edilmiş" color="default" />
                            : <Chip size="small" label="Aktif" color="success" />}
                        </TableCell>
                        <TableCell>
                          {cardHasPosBalance ? (
                            <Typography variant="caption" color="error.main" fontWeight="bold">
                              POS bakiye var - PYK&apos;dan iade edilmeli
                            </Typography>
                          ) : (
                            <>
                              <Typography variant="caption" color={
                                card.isReturned || card.depositRefunded ? 'text.secondary' :
                                card.depositPavoPaySaleNumber ? 'primary.main' : 'warning.main'
                              }>
                                {getRefundActionLabel(card)}
                              </Typography>
                              {balInfo && balInfo.totalBalance > 0 && !card.isReturned && (
                                <Typography variant="caption" display="block" color="warning.main" fontWeight="bold">
                                  + ₺{balInfo.totalBalance.toFixed(2)} nakit bakiye iade
                                </Typography>
                              )}
                            </>
                          )}
                        </TableCell>
                        <TableCell align="center">
                          {card.isReturned ? <Chip size="small" label="—" variant="outlined" />
                            : cardHasPosBalance ? <WarningIcon color="error" fontSize="small" />
                            : isSelected ? <CheckCircleIcon color="success" />
                            : <Chip size="small" label="Okutulmadı" color="warning" variant="outlined" />}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            {!hasPosBalance && (totalCashRefund > 0 || posDepositTotal > 0 || cashBalanceTotal > 0) && (
              <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover' }}>
                <Typography variant="subtitle2" gutterBottom>İade Özeti</Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  {cashDepositTotal > 0 && (
                    <Typography variant="body2"><strong>Nakit Deposito İade:</strong> ₺{cashDepositTotal.toFixed(2)}</Typography>
                  )}
                  {cashBalanceTotal > 0 && (
                    <Typography variant="body2"><strong>Nakit Bakiye İade:</strong> ₺{cashBalanceTotal.toFixed(2)}</Typography>
                  )}
                  {posDepositTotal > 0 && (
                    <Typography variant="body2" color="primary.main"><strong>POS Deposito İade:</strong> ₺{posDepositTotal.toFixed(2)}</Typography>
                  )}
                  <Typography variant="body2" fontWeight="bold">
                    Genel Toplam: ₺{(totalCashRefund + posDepositTotal).toFixed(2)}
                  </Typography>
                </Box>
              </Paper>
            )}

            {notYetScanned.length > 0 && !cashReturnDone && (
              <Alert severity="info" sx={{ mb: 2 }}>
                {notYetScanned.length} kart daha teslim alınabilir.
              </Alert>
            )}
          </>
        )}

        {step === 'PROCESSING' && (
          <Box sx={{ py: 2, textAlign: 'center' }}>
            <CircularProgress size={40} sx={{ mb: 1 }} />
            <Typography variant="subtitle1">İade işlemi devam ediyor...</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{processingMessage}</Typography>
          </Box>
        )}

        {step === 'DONE' && (
          <Box sx={{ py: 1 }}>
            <Alert severity={processResults.every(r => r.success) ? 'success' : 'warning'} sx={{ mb: 1, py: 0.5 }}>
              {processResults.every(r => r.success) ? 'İade işlemi başarılı' : 'İade işlemi tamamlandı'}
            </Alert>
            <List dense disablePadding sx={{ maxHeight: 120, overflow: 'auto' }}>
              {processResults.map((r, i) => (
                <ListItem key={i} disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 32 }}>
                    {r.success ? <CheckCircleIcon color="success" fontSize="small" /> : <WarningIcon color="error" fontSize="small" />}
                </ListItemIcon>
                  <ListItemText
                    primary={<Typography variant="body2" fontWeight="bold" sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{r.cardUID}</Typography>}
                    secondary={<Typography variant="caption">{r.message}</Typography>}
                  />
                </ListItem>
              ))}
            </List>
          </Box>
        )}
      </DialogContent>

      <Divider />

      <DialogActions sx={{ p: 2, flexWrap: 'wrap', gap: 1 }}>
        <Button onClick={onClose} disabled={step === 'PROCESSING'}>
          {step === 'DONE' ? t('cardReturnModal.close') : t('cardReturnModal.cancel')}
        </Button>

        {step === 'REVIEW' && !hasPosBalance && !cashReturnDone && (
          needsMixedButtons ? (
            <Button variant="contained" color="warning" onClick={handleCashReturn}
              disabled={returnedCardUIDs.size === 0}>
              Nakit İade + Teslim Al (₺{totalCashRefund.toFixed(2)})
            </Button>
          ) : (
            <Button variant="contained" color="secondary" onClick={handleAllInOneReturn}
              disabled={returnedCardUIDs.size === 0}>
              {returnedCardUIDs.size} Kartı Teslim Al
              {(totalCashRefund + posDepositTotal) > 0 && ` (₺${(totalCashRefund + posDepositTotal).toFixed(2)} İade)`}
            </Button>
          )
        )}

        {step === 'REVIEW' && !hasPosBalance && (cashReturnDone || needsMixedButtons) && posDepositTotal > 0 && (
          <Button variant="contained" color="primary" onClick={handlePosDepositReturn}
            disabled={!cashReturnDone}>
            POS Deposito İade (₺{posDepositTotal.toFixed(2)})
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default CardReturnModal;
